import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Box, Chip, CircularProgress, Fab, IconButton, Paper, TextField, Tooltip, Typography } from '@mui/material';
import HelpOutline from '@mui/icons-material/HelpOutline';
import CloseIcon from '@mui/icons-material/Close';
import SendOutlined from '@mui/icons-material/SendOutlined';
import DeleteSweepOutlined from '@mui/icons-material/DeleteSweepOutlined';
import { useLocation } from 'react-router-dom';
import { useTranslate } from 'react-admin';
import type { HelpMessage } from '../../services/helpBotService';
import tokenService from '../../services/tokenService';
import './help-bot.css';

// Dynamic import keeps axios (ESM) out of the Layout test graph — same pattern as ExtendedBlockViews.
const service = async () => (await import('../../services/helpBotService')).helpBotService;

interface ChatMessage extends HelpMessage {
    pending?: boolean;
}

/** Context suggestions per route (Tranche D2, #489) — keys of helpBot.suggestions.* */
const suggestionsFor = (pathname: string): string[] => {
    if (pathname.startsWith('/course-player')) return ['player', 'notes', 'tutor'];
    if (pathname.startsWith('/catalog') || pathname.startsWith('/my-courses')) return ['enroll', 'assignments', 'certificates'];
    if (pathname.startsWith('/courses') || pathname.startsWith('/lesson')) return ['createCourse', 'lessons', 'aiGenerator'];
    if (pathname.startsWith('/course_assignments')) return ['assignmentCreate', 'reminders', 'overdue'];
    if (pathname.startsWith('/knowledge-base') || pathname.startsWith('/ai-settings')) return ['knowledgeBase', 'aiSettings', 'aiGenerator'];
    if (pathname.startsWith('/invitations') || pathname.startsWith('/branding')) return ['invite', 'roles', 'branding'];
    return ['start', 'certificates', 'notifications'];
};

/**
 * Floating help chatbot (Tranche D2, #489): bottom-right button, panel with
 * streamed answers, route-aware suggestions. Hidden unless the feature is
 * licensed (`ai.help_bot`) and enabled for the company.
 */
export const HelpBotWidget: React.FC = () => {
    const translate = useTranslate();
    const { pathname } = useLocation();
    const [enabled, setEnabled] = useState(false);
    const [open, setOpen] = useState(false);
    const [messages, setMessages] = useState<ChatMessage[]>([]);
    const [input, setInput] = useState('');
    const [busy, setBusy] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const abortRef = useRef<AbortController | null>(null);
    const endRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
        if (!tokenService.getToken() || !tokenService.hasFeature('ai.help_bot')) {
            setEnabled(false);
            return;
        }
        let cancelled = false;
        service().then((svc) => svc.isEnabled()).then((ok) => { if (!cancelled) setEnabled(ok); }).catch(() => { if (!cancelled) setEnabled(false); });
        return () => { cancelled = true; };
    }, []);

    useEffect(() => { endRef.current?.scrollIntoView?.({ behavior: 'smooth' }); }, [messages]);
    useEffect(() => () => abortRef.current?.abort(), []);

    const send = useCallback(async (text: string) => {
        const question = text.trim();
        if (!question || busy) return;
        setError(null);
        setInput('');
        const history: HelpMessage[] = [...messages.filter((m) => !m.pending).map(({ role, content }) => ({ role, content })), { role: 'user', content: question }];
        setMessages([...history, { role: 'assistant', content: '', pending: true }]);
        setBusy(true);
        const controller = new AbortController();
        abortRef.current = controller;
        try {
            await (await service()).chat(history, (delta) => {
                setMessages((prev) => {
                    const next = [...prev];
                    const last = next[next.length - 1];
                    if (last?.pending) next[next.length - 1] = { ...last, content: last.content + delta };
                    return next;
                });
            }, controller.signal);
            setMessages((prev) => prev.map((m) => (m.pending ? { ...m, pending: false } : m)));
        } catch (e) {
            if (!controller.signal.aborted) {
                setError(e instanceof Error ? e.message : translate('helpBot.error'));
                setMessages((prev) => prev.filter((m) => !m.pending));
            }
        } finally {
            abortRef.current = null;
            setBusy(false);
        }
    }, [busy, messages, translate]);

    if (!enabled) return null;

    return (
        <>
            {open && (
                <Paper className="oa-helpbot-panel" elevation={8} role="dialog" aria-label={translate('helpBot.title')} data-testid="helpbot-panel">
                    <Box className="oa-helpbot-head">
                        <HelpOutline fontSize="small" />
                        <Typography variant="subtitle1" sx={{ fontWeight: 700, flex: 1 }}>{translate('helpBot.title')}</Typography>
                        <Chip size="small" label={translate('helpBot.aiLabel')} variant="outlined" />
                        <Tooltip title={translate('helpBot.clear')}><span><IconButton size="small" onClick={() => { abortRef.current?.abort(); setMessages([]); setError(null); }} disabled={messages.length === 0} aria-label={translate('helpBot.clear')}><DeleteSweepOutlined fontSize="small" /></IconButton></span></Tooltip>
                        <IconButton size="small" onClick={() => setOpen(false)} aria-label={translate('helpBot.close')}><CloseIcon fontSize="small" /></IconButton>
                    </Box>
                    <Box className="oa-helpbot-body">
                        {messages.length === 0 && (
                            <Box>
                                <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>{translate('helpBot.intro')}</Typography>
                                <Box className="oa-helpbot-suggestions">
                                    {suggestionsFor(pathname).map((key) => (
                                        <Chip key={key} size="small" clickable label={translate(`helpBot.suggestions.${key}`)} onClick={() => void send(translate(`helpBot.suggestions.${key}`))} />
                                    ))}
                                </Box>
                            </Box>
                        )}
                        {messages.map((m, i) => (
                            <div key={i} className={`oa-helpbot-msg oa-helpbot-msg--${m.role}`}>
                                {m.content}{m.pending && m.content === '' && <CircularProgress size={14} sx={{ ml: 1 }} />}
                            </div>
                        ))}
                        {error && <Typography variant="body2" color="error">{error}</Typography>}
                        <div ref={endRef} />
                    </Box>
                    <Box className="oa-helpbot-input">
                        <TextField
                            fullWidth
                            size="small"
                            placeholder={translate('helpBot.placeholder')}
                            value={input}
                            onChange={(e) => setInput(e.target.value)}
                            onKeyDown={(e) => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); void send(input); } }}
                            disabled={busy}
                            inputProps={{ 'aria-label': translate('helpBot.placeholder'), maxLength: 2000 }}
                        />
                        <IconButton color="primary" onClick={() => void send(input)} disabled={busy || input.trim() === ''} aria-label={translate('helpBot.send')}><SendOutlined /></IconButton>
                    </Box>
                    <Typography variant="caption" color="text.secondary" sx={{ px: 1.5, pb: 1 }}>{translate('helpBot.disclaimer')}</Typography>
                </Paper>
            )}
            <Tooltip title={translate('helpBot.title')}>
                <Fab color="primary" size="medium" className="oa-helpbot-fab" onClick={() => setOpen((o) => !o)} aria-label={translate('helpBot.title')} data-testid="helpbot-fab">
                    {open ? <CloseIcon /> : <HelpOutline />}
                </Fab>
            </Tooltip>
        </>
    );
};

export default HelpBotWidget;
